import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';

interface FeatureAdsConfig {
  isAllowAds: boolean;
  adType: 'none' | 'interstitial' | 'rewarded';
  // Feature-specific ad unit IDs (optional)
  // If not set, will fallback to shared global ad units from AdConfig
  interstitialAdUnitId?: {
    android?: string;
    ios?: string;
  };
  rewardedAdUnitId?: {
    android?: string;
    ios?: string;
  };
}

interface FeatureCreditsConfig {
  isAllowCreditUsage: boolean;
  creditCost: number;
}

interface FeatureDailyQuotaConfig {
  isDailyLimitForFreeUsers: boolean;
  dailyQuota: number;
}

interface FeatureConfig {
  id?: string;
  featureId: string;
  ads: FeatureAdsConfig;
  credits: FeatureCreditsConfig;
  dailyQuota: FeatureDailyQuotaConfig;
  isActive?: boolean;
  version?: number;
  lastUpdatedBy?: {
    adminId: string;
    adminName: string;
    adminEmail: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

const FEATURE_NAMES: Record<string, string> = {
  photoSwap: 'Single Face Swap',
  groupPhotoPerFace: 'Group Photo Swap',
  videoSwap: 'Video Face Swap',
  watermarkRemoval: 'Watermark Removal',
  premiumTemplate: 'Premium Template',
  enhanceImage: 'Enhance Image',
  restoreImage: 'Restore Old Image',
};

const FeatureConfiguration: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [configs, setConfigs] = useState<FeatureConfig[]>([]);
  const [expandedFeature, setExpandedFeature] = useState<string | false>(false);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getAllFeatureConfigs();

      if (response.success && response.data) {
        setConfigs(response.data);
        // Expand first feature by default
        if (response.data.length > 0) {
          setExpandedFeature(response.data[0].featureId);
        }
      } else {
        setError(response.error || 'Failed to fetch feature configurations');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch feature configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleAdsChange = (featureId: string, field: keyof FeatureAdsConfig, value: any) => {
    setConfigs((prev) =>
      prev.map((config) => {
        if (config.featureId === featureId) {
          const newAds = { ...config.ads, [field]: value };
          // If ads are disabled, set adType to 'none'
          if (field === 'isAllowAds' && value === false) {
            newAds.adType = 'none';
          }
          return { ...config, ads: newAds };
        }
        return config;
      })
    );
  };

  const handleCreditsChange = (featureId: string, field: keyof FeatureCreditsConfig, value: any) => {
    setConfigs((prev) =>
      prev.map((config) => {
        if (config.featureId === featureId) {
          return {
            ...config,
            credits: { ...config.credits, [field]: value },
          };
        }
        return config;
      })
    );
  };

  const handleDailyQuotaChange = (featureId: string, field: keyof FeatureDailyQuotaConfig, value: any) => {
    setConfigs((prev) =>
      prev.map((config) => {
        if (config.featureId === featureId) {
          return {
            ...config,
            dailyQuota: { ...config.dailyQuota, [field]: value },
          };
        }
        return config;
      })
    );
  };

  const handleSave = async (featureId: string) => {
    try {
      setSaving(featureId);
      setError('');

      const config = configs.find((c) => c.featureId === featureId);
      if (!config) {
        throw new Error('Configuration not found');
      }

      const response = await apiService.updateFeatureConfig(featureId, {
        ads: config.ads,
        credits: config.credits,
        dailyQuota: config.dailyQuota,
      });

      if (response.success) {
        toast.success(`${FEATURE_NAMES[featureId]} configuration updated successfully`);
        await fetchConfigs(); // Refresh to get updated version
      } else {
        setError(response.error || 'Failed to update feature configuration');
        toast.error(response.error || 'Failed to update feature configuration');
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to update feature configuration';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(null);
    }
  };

  const handleSaveAll = async () => {
    for (const config of configs) {
      await handleSave(config.featureId);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Feature Configuration</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchConfigs}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveAll}
            disabled={saving !== null}
          >
            Save All
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure ads, credits, and daily quota settings for each feature. These settings are available via v2 API endpoints.
      </Typography>

      {configs.map((config) => (
        <Accordion
          key={config.featureId}
          expanded={expandedFeature === config.featureId}
          onChange={(_, isExpanded) => setExpandedFeature(isExpanded ? config.featureId : false)}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
              <Typography variant="h6">{FEATURE_NAMES[config.featureId] || config.featureId}</Typography>
              {config.version && (
                <Typography variant="body2" color="text.secondary">
                  v{config.version}
                </Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* Ad Configuration */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Ad Configuration
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.ads.isAllowAds}
                          onChange={(e) => handleAdsChange(config.featureId, 'isAllowAds', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Allow Ads"
                      sx={{ mb: 2 }}
                    />

                    {config.ads.isAllowAds && (
                      <>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel>Ad Type</InputLabel>
                          <Select
                            value={config.ads.adType}
                            onChange={(e) => handleAdsChange(config.featureId, 'adType', e.target.value)}
                            label="Ad Type"
                          >
                            <MenuItem value="none">No Ad</MenuItem>
                            <MenuItem value="interstitial">Interstitial Ad</MenuItem>
                            <MenuItem value="rewarded">Rewarded Ad</MenuItem>
                          </Select>
                        </FormControl>

                        {config.ads.adType !== 'none' && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                              Feature-Specific Ad Unit IDs (Optional)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Configure custom ad unit IDs for this feature. If left empty, the feature will use shared global ad units from Ad Configuration settings.
                            </Typography>

                            {config.ads.adType === 'interstitial' ? (
                              <>
                                <TextField
                                  fullWidth
                                  label="Android Interstitial Ad Unit ID"
                                  value={config.ads.interstitialAdUnitId?.android || ''}
                                  onChange={(e) => {
                                    setConfigs((prev) =>
                                      prev.map((c) => {
                                        if (c.featureId === config.featureId) {
                                          return {
                                            ...c,
                                            ads: {
                                              ...c.ads,
                                              interstitialAdUnitId: {
                                                ...c.ads.interstitialAdUnitId,
                                                android: e.target.value,
                                              },
                                            },
                                          };
                                        }
                                        return c;
                                      })
                                    );
                                  }}
                                  placeholder="Leave empty to use shared global ad unit"
                                  helperText="e.g., ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                                  sx={{ mb: 2 }}
                                />
                                <TextField
                                  fullWidth
                                  label="iOS Interstitial Ad Unit ID"
                                  value={config.ads.interstitialAdUnitId?.ios || ''}
                                  onChange={(e) => {
                                    setConfigs((prev) =>
                                      prev.map((c) => {
                                        if (c.featureId === config.featureId) {
                                          return {
                                            ...c,
                                            ads: {
                                              ...c.ads,
                                              interstitialAdUnitId: {
                                                ...c.ads.interstitialAdUnitId,
                                                ios: e.target.value,
                                              },
                                            },
                                          };
                                        }
                                        return c;
                                      })
                                    );
                                  }}
                                  placeholder="Leave empty to use shared global ad unit"
                                  helperText="e.g., ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                                  sx={{ mb: 2 }}
                                />
                              </>
                            ) : (
                              <>
                                <TextField
                                  fullWidth
                                  label="Android Rewarded Ad Unit ID"
                                  value={config.ads.rewardedAdUnitId?.android || ''}
                                  onChange={(e) => {
                                    setConfigs((prev) =>
                                      prev.map((c) => {
                                        if (c.featureId === config.featureId) {
                                          return {
                                            ...c,
                                            ads: {
                                              ...c.ads,
                                              rewardedAdUnitId: {
                                                ...c.ads.rewardedAdUnitId,
                                                android: e.target.value,
                                              },
                                            },
                                          };
                                        }
                                        return c;
                                      })
                                    );
                                  }}
                                  placeholder="Leave empty to use shared global ad unit"
                                  helperText="e.g., ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                                  sx={{ mb: 2 }}
                                />
                                <TextField
                                  fullWidth
                                  label="iOS Rewarded Ad Unit ID"
                                  value={config.ads.rewardedAdUnitId?.ios || ''}
                                  onChange={(e) => {
                                    setConfigs((prev) =>
                                      prev.map((c) => {
                                        if (c.featureId === config.featureId) {
                                          return {
                                            ...c,
                                            ads: {
                                              ...c.ads,
                                              rewardedAdUnitId: {
                                                ...c.ads.rewardedAdUnitId,
                                                ios: e.target.value,
                                              },
                                            },
                                          };
                                        }
                                        return c;
                                      })
                                    );
                                  }}
                                  placeholder="Leave empty to use shared global ad unit"
                                  helperText="e.g., ca-app-pub-XXXXXXXXXXXXXXXX/XXXXXXXXXX"
                                  sx={{ mb: 2 }}
                                />
                              </>
                            )}

                          <Alert severity="info" sx={{ mt: 2 }}>
                              {(() => {
                                const hasCustomIds = config.ads.adType === 'interstitial'
                                  ? (config.ads.interstitialAdUnitId?.android || config.ads.interstitialAdUnitId?.ios)
                                  : (config.ads.rewardedAdUnitId?.android || config.ads.rewardedAdUnitId?.ios);
                                
                                if (hasCustomIds) {
                                  return `This feature will use its own custom ad unit IDs. If a platform-specific ID is not set, it will fallback to the shared global ad unit for that platform.`;
                                } else {
                                  return `This feature will use the shared global ${config.ads.adType === 'interstitial' ? 'interstitial' : 'rewarded'} ad unit from Ad Configuration settings.`;
                                }
                              })()}
                          </Alert>
                          </>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Credit Configuration */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Credit Configuration
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.credits.isAllowCreditUsage}
                          onChange={(e) => handleCreditsChange(config.featureId, 'isAllowCreditUsage', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Allow Credit Usage"
                      sx={{ mb: 2 }}
                    />

                    {config.credits.isAllowCreditUsage && (
                      <TextField
                        fullWidth
                        label="Credit Cost"
                        type="number"
                        value={config.credits.creditCost}
                        onChange={(e) => handleCreditsChange(config.featureId, 'creditCost', parseInt(e.target.value) || 0)}
                        helperText="Number of credits required for this operation"
                        inputProps={{ min: 0 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Daily Quota Configuration */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Daily Quota (Free Users)
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.dailyQuota.isDailyLimitForFreeUsers}
                          onChange={(e) => handleDailyQuotaChange(config.featureId, 'isDailyLimitForFreeUsers', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Enable Daily Limit"
                      sx={{ mb: 2 }}
                    />

                    {config.dailyQuota.isDailyLimitForFreeUsers && (
                      <TextField
                        fullWidth
                        label="Daily Quota"
                        type="number"
                        value={config.dailyQuota.dailyQuota}
                        onChange={(e) => handleDailyQuotaChange(config.featureId, 'dailyQuota', parseInt(e.target.value) || 0)}
                        helperText="Number of free operations per day (watch ad without using credits)"
                        inputProps={{ min: 0 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Save Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  {config.lastUpdatedBy && (
                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      Last updated: {config.updatedAt ? new Date(config.updatedAt).toLocaleString() : 'N/A'}
                      {config.lastUpdatedBy.adminEmail && ` by ${config.lastUpdatedBy.adminEmail}`}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => handleSave(config.featureId)}
                    disabled={saving === config.featureId}
                  >
                    {saving === config.featureId ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default FeatureConfiguration;


